import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { questionAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { FormattedText } from '../utils/formatText';

function QuestionDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchQuestion();
    // eslint-disable-next-line
  }, [id]);

  const fetchQuestion = async () => {
    try {
      setLoading(true);
      const response = await questionAPI.getQuestion(id);
      console.log('API Response:', response.data);
      // The API returns { success: true, question: {...} }
      const questionData = response.data.question || response.data;
      console.log('Question data:', questionData);
      setQuestion(questionData);
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to fetch question', 'error');
      navigate('/question-bank');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/question-bank?edit=${id}`);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    try {
      await questionAPI.deleteQuestion(id);
      showToast('Question deleted successfully', 'success');
      navigate('/question-bank');
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to delete question', 'error');
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '40px' }}>
        <p>Loading question...</p>
      </div>
    );
  }

  if (!question) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '40px' }}>
        <p>Question not found</p>
        <button className="btn btn-primary" onClick={() => navigate('/question-bank')}>
          Back to Question Bank
        </button>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card" style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Back Button */}
        <button
          onClick={() => navigate('/question-bank')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#f7fafc',
            border: '1.5px solid #e2e8f0',
            borderRadius: '6px',
            fontSize: '14px',
            cursor: 'pointer',
            fontWeight: '600',
            color: '#4a5568',
            marginBottom: '20px'
          }}
        >
          ← Back to Question Bank
        </button>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
            <span style={{
              padding: '6px 12px',
              backgroundColor: question.difficulty === 'Easy' ? '#c6f6d5' : question.difficulty === 'Medium' ? '#fef3c7' : '#fecaca',
              color: question.difficulty === 'Easy' ? '#22543d' : question.difficulty === 'Medium' ? '#78350f' : '#7f1d1d',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '700'
            }}>
              {String(question.difficulty || '')}
            </span>
            <span style={{
              padding: '6px 12px',
              backgroundColor: '#eef2ff',
              color: '#667eea',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '700'
            }}>
              {String(question.questionType || '')}
            </span>
            {question.category && (
              Array.isArray(question.category) ? (
                question.category.map((cat, catIdx) => (
                  <span 
                    key={catIdx}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#e6fffa',
                      color: '#234e52',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '700'
                    }}
                  >
                    {typeof cat === 'object' && cat?.name ? cat.name : String(cat)}
                  </span>
                ))
              ) : (
                <span 
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#e6fffa',
                    color: '#234e52',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '700'
                  }}
                >
                  {typeof question.category === 'object' && question.category?.name ? question.category.name : String(question.category)}
                </span>
              )
            )}
          </div>

          <h2 style={{ margin: '0 0 16px 0', fontSize: '24px', color: '#2d3748', lineHeight: '1.6' }}>
            <FormattedText text={String(question.question || '')} />
          </h2>

          {question.tags && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {Array.isArray(question.tags) ? (
                question.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    style={{
                      padding: '4px 12px',
                      backgroundColor: '#f0f4ff',
                      color: '#667eea',
                      borderRadius: '12px',
                      fontSize: '13px',
                      fontWeight: '500',
                      border: '1px solid #dae3ff'
                    }}
                  >
                    {typeof tag === 'object' && tag?.name ? tag.name : String(tag)}
                  </span>
                ))
              ) : (
                <span
                  style={{
                    padding: '4px 12px',
                    backgroundColor: '#f0f4ff',
                    color: '#667eea',
                    borderRadius: '12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    border: '1px solid #dae3ff'
                  }}
                >
                  {typeof question.tags === 'object' && question.tags?.name ? question.tags.name : String(question.tags)}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Options */}
        {question.options && Array.isArray(question.options) && question.options.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#4a5568', marginBottom: '16px' }}>
              Answer Options:
            </h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              {question.options.map((option, optIdx) => (
                <div
                  key={optIdx}
                  style={{
                    padding: '14px 18px',
                    borderRadius: '8px',
                    fontSize: '15px',
                    backgroundColor: optIdx === question.correctAnswer ? '#c6f6d5' : '#f7fafc',
                    border: optIdx === question.correctAnswer ? '2px solid #48bb78' : '1.5px solid #e2e8f0',
                    color: optIdx === question.correctAnswer ? '#2f855a' : '#4a5568',
                    fontWeight: optIdx === question.correctAnswer ? '600' : '500'
                  }}
                >
                  <span style={{ fontWeight: '700', marginRight: '10px', fontSize: '16px' }}>
                    {String.fromCharCode(65 + optIdx)}.
                  </span>
                  <FormattedText text={String(option)} />
                  {optIdx === question.correctAnswer && (
                    <span style={{ 
                      marginLeft: '12px', 
                      fontSize: '18px',
                      fontWeight: '700'
                    }}>✓ Correct</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Explanation */}
        {question.explanation && (
          <div style={{
            marginBottom: '24px',
            padding: '18px',
            backgroundColor: '#fffbeb',
            borderLeft: '4px solid #f59e0b',
            borderRadius: '8px'
          }}>
            <h3 style={{ 
              fontSize: '17px', 
              fontWeight: '700', 
              color: '#92400e', 
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>📖</span> Explanation
            </h3>
            <p style={{ margin: 0, fontSize: '15px', color: '#78350f', lineHeight: '1.7' }}>
              {String(question.explanation || '')}
            </p>
          </div>
        )}

        {/* Points to Remember */}
        {question.pointsToRemember && Array.isArray(question.pointsToRemember) && question.pointsToRemember.length > 0 && (
          <div style={{
            marginBottom: '24px',
            padding: '18px',
            backgroundColor: '#f0f9ff',
            borderLeft: '4px solid #3b82f6',
            borderRadius: '8px'
          }}>
            <h3 style={{ 
              fontSize: '17px', 
              fontWeight: '700', 
              color: '#1e40af', 
              marginBottom: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>💡</span> Points to Remember
            </h3>
            <ul style={{ 
              margin: 0, 
              paddingLeft: '24px',
              fontSize: '15px',
              color: '#1e40af',
              lineHeight: '1.8'
            }}>
              {question.pointsToRemember.map((point, idx) => (
                <li key={idx} style={{ marginBottom: '8px' }}>
                  {String(point)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Interviewer Points */}
        {question.interviewerPoints && Array.isArray(question.interviewerPoints) && question.interviewerPoints.length > 0 && (
          <div style={{
            marginBottom: '24px',
            padding: '18px',
            backgroundColor: '#fdf4ff',
            borderLeft: '4px solid #a855f7',
            borderRadius: '8px'
          }}>
            <h3 style={{ 
              fontSize: '17px', 
              fontWeight: '700', 
              color: '#7e22ce', 
              marginBottom: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>🎯</span> What the interviewer is looking for
            </h3>
            <ul style={{ 
              margin: 0, 
              paddingLeft: '24px',
              fontSize: '15px',
              color: '#7e22ce',
              lineHeight: '1.8'
            }}>
              {question.interviewerPoints.map((point, idx) => (
                <li key={idx} style={{ marginBottom: '8px' }}>
                  {String(point)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Admin Actions */}
        {isAdmin && (
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            marginTop: '28px', 
            paddingTop: '24px', 
            borderTop: '2px solid #e2e8f0' 
          }}>
            <button
              onClick={handleEdit}
              className="btn btn-primary"
              style={{ flex: 1 }}
            >
              ✏️ Edit Question
            </button>
            <button
              onClick={handleDelete}
              className="btn btn-danger"
              style={{ flex: 1 }}
            >
              🗑️ Delete Question
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default QuestionDetails;
